import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import GroupIcon from '@mui/icons-material/Group';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const ADMIN_TASK_FIELDS = [
  { id: 'all', name: 'All tasks' },
  ...Array.from({ length: 10 }, (_, i) => ({ id: `task${i + 1}`, name: `Task ${i + 1}` }))
];

const ADMIN_AUDIENCE_FIELDS = [
  { id: 'core_jobs', name: 'core jobs' },
  { id: 'contexts', name: 'contexts' },
  { id: 'fears', name: 'fears' },
  { id: 'bigJobs', name: 'bigJobs' },
  { id: 'subtasks', name: 'subtasks' },
  { id: 'competitors', name: 'competitors' },
  { id: 'habits', name: 'habits' },
];

const ADMIN_AUDIENCES = [
  {
    id: 'all_audience',
    name: 'All audience',
    children: ADMIN_AUDIENCE_FIELDS.map(f => ({ id: `all_audience_${f.id}`, name: f.name })),
  },
  ...Array.from({ length: 10 }, (_, i) => ({
    id: `audience${i + 1}`,
    name: `Audience ${i + 1}`,
    children: ADMIN_AUDIENCE_FIELDS.map(f => ({ id: `audience${i + 1}_${f.id}`, name: f.name })),
  })),
];

function AdminDataReferenceTree({ value = [], onChange }) {
  const [selection, setSelection] = useState(Array.isArray(value) ? value : []);
  const [activeTab, setActiveTab] = useState(0); // 0 = Audiences, 1 = Tasks
  const [expandedAudiences, setExpandedAudiences] = useState({});

  const handleToggle = (id) => {
    let newSelection;
    if (selection.includes(id)) {
      newSelection = selection.filter(sel => sel !== id);
    } else {
      newSelection = [...selection, id];
    }
    setSelection(newSelection);
    if (onChange) onChange(newSelection);
  };

  const handleAudienceToggle = (audId, fieldIds) => {
    const allFieldsSelected = fieldIds.every(fid => selection.includes(fid));
    let newSelection = [...selection];
    if (allFieldsSelected) {
      // Deselect all fields and the parent
      newSelection = newSelection.filter(sel => sel !== audId && !fieldIds.includes(sel));
    } else {
      // Select all fields and the parent
      newSelection = Array.from(new Set([...newSelection, audId, ...fieldIds]));
    }
    setSelection(newSelection);
    if (onChange) onChange(newSelection);
  };

  const handleExpandAudience = (audId) => {
    setExpandedAudiences(prev => ({ ...prev, [audId]: !prev[audId] }));
  };

  return (
    <Paper sx={{ p: 2, minWidth: 320 }}>
      <Typography variant="h6" gutterBottom>Admin Data Reference</Typography>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab icon={<GroupIcon />} label="Admin Audiences" />
          <Tab icon={<AssignmentIcon />} label="Admin Tasks" />
        </Tabs>
      </Box>
      <Box role="tabpanel" hidden={activeTab !== 0}>
        {activeTab === 0 && (
          <List>
            {ADMIN_AUDIENCES.map(aud => {
              const fieldIds = aud.children.map(child => child.id);
              const allFieldsSelected = fieldIds.every(fid => selection.includes(fid));
              const someFieldsSelected = fieldIds.some(fid => selection.includes(fid));
              return (
                <React.Fragment key={aud.id}>
                  <ListItem button onClick={() => handleExpandAudience(aud.id)} sx={{ pl: 1 }}>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={allFieldsSelected}
                        indeterminate={someFieldsSelected && !allFieldsSelected}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ 'aria-labelledby': `admin-audience-${aud.id}` }}
                        onClick={e => { e.stopPropagation(); handleAudienceToggle(aud.id, fieldIds); }}
                      />
                    </ListItemIcon>
                    <ListItemIcon sx={{ minWidth: 'auto' }}>
                      {expandedAudiences[aud.id] ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                    </ListItemIcon>
                    <ListItemText id={`admin-audience-${aud.id}`} primary={aud.name} />
                  </ListItem>
                  {expandedAudiences[aud.id] && aud.children && aud.children.map(child => (
                    <ListItem key={child.id} button onClick={() => handleToggle(child.id)} sx={{ pl: 5 }}>
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selection.includes(child.id)}
                          tabIndex={-1}
                          disableRipple
                          inputProps={{ 'aria-labelledby': `admin-audience-child-${child.id}` }}
                        />
                      </ListItemIcon>
                      <ListItemText id={`admin-audience-child-${child.id}`} primary={child.name} />
                    </ListItem>
                  ))}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>
      <Box role="tabpanel" hidden={activeTab !== 1}>
        {activeTab === 1 && (
          <List>
            {ADMIN_TASK_FIELDS.map(task => (
              <ListItem key={task.id} button onClick={() => handleToggle(task.id)}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selection.includes(task.id)}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': `admin-task-${task.id}` }}
                  />
                </ListItemIcon>
                <ListItemIcon>
                  <AssignmentIcon />
                </ListItemIcon>
                <ListItemText id={`admin-task-${task.id}`} primary={task.name} />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Paper>
  );
}

AdminDataReferenceTree.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func,
};

export default AdminDataReferenceTree; 